import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star, ShoppingBag } from "lucide-react";

const TYPE_LABELS = {
  rental_per_day: "Leie per dag",
  rental_per_booking: "Leie per booking",
  purchase: "Kjøp",
};

function ProductEditor({ open, onOpenChange, initial, onSaved }) {
  const isNew = !initial;
  const [form, setForm] = useState({});

  useEffect(() => {
    if (open) {
      setForm({
        name_no: initial?.name_no || "",
        name_en: initial?.name_en || "",
        description_no: initial?.description_no || "",
        description_en: initial?.description_en || "",
        type: initial?.type || "rental_per_booking",
        unit_price_eur: initial?.unit_price_eur ?? 0,
        recommended: !!initial?.recommended,
        sort_order: initial?.sort_order ?? 100,
        active: initial ? !!initial.active : true,
      });
    }
  }, [open, initial]);

  const save = async () => {
    if (!form.name_no?.trim()) return toast.error("Skriv inn et navn (norsk)");
    if (form.unit_price_eur < 0) return toast.error("Pris kan ikke være negativ");
    const body = {
      ...form,
      name_no: form.name_no.trim(),
      name_en: form.name_en?.trim() || null,
      description_no: form.description_no || "",
      description_en: form.description_en || "",
      unit_price_eur: parseInt(form.unit_price_eur, 10) || 0,
      sort_order: parseInt(form.sort_order, 10) || 0,
    };
    try {
      if (isNew) {
        await api.post("/products", body);
        toast.success("Produkt opprettet");
      } else {
        await api.patch(`/products/${initial.id}`, body);
        toast.success("Produkt oppdatert");
      }
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke lagre");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? "Nytt produkt" : `Rediger «${initial?.name_no}»`}</DialogTitle>
          <DialogDescription>
            Produkter vises i kundeportalen under «Tilleggstjenester». Kunden kan
            selv legge dem til bookingen sin.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Navn (norsk)</Label>
              <Input
                value={form.name_no || ""}
                onChange={(e) => setForm({ ...form, name_no: e.target.value })}
                data-testid="product-editor-name-no"
              />
            </div>
            <div>
              <Label>Navn (engelsk)</Label>
              <Input
                value={form.name_en || ""}
                onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                data-testid="product-editor-name-en"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Beskrivelse (norsk)</Label>
              <Textarea
                value={form.description_no || ""}
                onChange={(e) => setForm({ ...form, description_no: e.target.value })}
                rows={2}
                data-testid="product-editor-desc-no"
              />
            </div>
            <div>
              <Label>Beskrivelse (engelsk)</Label>
              <Textarea
                value={form.description_en || ""}
                onChange={(e) => setForm({ ...form, description_en: e.target.value })}
                rows={2}
                data-testid="product-editor-desc-en"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr] gap-3">
            <div>
              <Label>Type</Label>
              <Select
                value={form.type || "rental_per_booking"}
                onValueChange={(v) => setForm({ ...form, type: v })}
              >
                <SelectTrigger data-testid="product-editor-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABELS).map(([k, l]) => (
                    <SelectItem key={k} value={k}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Pris (€)</Label>
              <Input
                type="number"
                min={0}
                value={form.unit_price_eur ?? 0}
                onChange={(e) => setForm({ ...form, unit_price_eur: e.target.value })}
                data-testid="product-editor-price"
              />
            </div>
            <div>
              <Label>Sortering</Label>
              <Input
                type="number"
                value={form.sort_order ?? 100}
                onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                data-testid="product-editor-sort"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label
              className="flex items-center gap-2 p-3 rounded-md border border-slate-200 hover:border-[#BD984D]/40 cursor-pointer"
              data-testid="product-editor-recommended"
            >
              <Switch
                checked={!!form.recommended}
                onCheckedChange={(v) => setForm({ ...form, recommended: v })}
              />
              <Star className="w-4 h-4 text-[#BD984D]" />
              <span className="text-sm">Anbefal (vises med ★ i kundeportalen)</span>
            </label>
            <label
              className="flex items-center gap-2 p-3 rounded-md border border-slate-200 cursor-pointer"
              data-testid="product-editor-active"
            >
              <Switch
                checked={!!form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })}
              />
              <span className="text-sm">Aktiv – vises for kunder</span>
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Avbryt</Button>
          <Button
            onClick={save}
            data-testid="product-editor-save"
            className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
          >
            {isNew ? "Opprett produkt" : "Lagre"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ProductsManager() {
  const [items, setItems] = useState([]);
  const [editor, setEditor] = useState({ open: false, product: null });

  const load = async () => {
    const r = await api.get("/products");
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!window.confirm(`Skjule «${p.name_no}» fra kundeportalen?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Produkt deaktivert");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke deaktivere");
    }
  };

  return (
    <div className="space-y-4" data-testid="products-manager">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-[#2B3F4D] max-w-2xl">
          Produkter vises i kundens portal under «Tilleggstjenester». Kunden
          legger dem til bookingen og betaler ved ankomst (Stripe-betaling skrur
          jeg på når nøkkel er på plass).
        </p>
        <Button
          onClick={() => setEditor({ open: true, product: null })}
          data-testid="products-add"
          className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
        >
          <Plus className="w-4 h-4 mr-1.5" /> Nytt produkt
        </Button>
      </div>

      <div className="space-y-2">
        {items.length === 0 && (
          <Card className="p-6 text-center text-sm text-[#2B3F4D]">
            Ingen produkter ennå. Opprett ditt første over.
          </Card>
        )}
        {items.map((p) => (
          <Card key={p.id} className="p-4" data-testid={`products-row-${p.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-[#0C1824] flex items-center gap-2 flex-wrap">
                  <ShoppingBag className="w-4 h-4 text-[#1DA3B3]" />
                  {p.name_no}
                  <Badge className="bg-[#BD984D]/15 text-[#A57F39] text-[10px]">
                    {TYPE_LABELS[p.type]} · {p.unit_price_eur} €
                  </Badge>
                  {p.recommended && (
                    <Badge className="bg-[#BD984D] text-white text-[10px]">
                      <Star className="w-3 h-3 mr-0.5" /> Anbefalt
                    </Badge>
                  )}
                  {!p.active && (
                    <Badge className="bg-slate-200 text-slate-600 text-[10px]">Inaktiv</Badge>
                  )}
                </div>
                {p.description_no && (
                  <div className="text-xs text-[#2B3F4D] mt-1 max-w-xl">
                    {p.description_no}
                  </div>
                )}
              </div>
              <div className="flex gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditor({ open: true, product: p })}
                  data-testid={`products-edit-${p.id}`}
                  className="border-[#0C1824] text-[#0C1824] h-9"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                {p.active && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => remove(p)}
                    data-testid={`products-delete-${p.id}`}
                    className="border-red-200 text-red-600 hover:bg-red-50 h-9"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      <ProductEditor
        open={editor.open}
        onOpenChange={(o) => setEditor({ open: o, product: editor.product })}
        initial={editor.product}
        onSaved={load}
      />
    </div>
  );
}
